'use client';

import React from 'react';
import { LucideIcon } from 'lucide-react';

// ──────────────────────────────────────────────────────────────
// Section Types & Variations
// ──────────────────────────────────────────────────────────────

export type SectionCategory =
  | 'header'
  | 'hero'
  | 'fleet'
  | 'features'
  | 'testimonials'
  | 'pricing'
  | 'contact'
  | 'faq'
  | 'gallery'
  | 'steps'
  | 'stats'
  | 'location'
  | 'cta'
  | 'footer'
  | 'custom';

export type PreviewBreakpoint = 'desktop' | 'tablet' | 'mobile';

export interface SectionVariation {
  id: string;
  name: string;
  description: string;
  thumbnail: string; // SVG or emoji representation
  component: React.ComponentType<SectionProps>;
  defaultProps: Record<string, any>;
  editableFields: EditableField[];
}

export interface SectionTemplate {
  category: SectionCategory;
  label: string;
  icon: LucideIcon;
  description: string;
  variations: SectionVariation[];
}

export interface SectionInstance {
  id: string;
  category: SectionCategory;
  variationId: string;
  props: Record<string, any>;
  order: number;
  style?: SectionStyle;
}

/** Optional overrides applied on tablet/mobile preview and published CSS. */
export type SectionStylePatch = Partial<Pick<
  SectionStyle,
  | 'backgroundType'
  | 'backgroundColor'
  | 'backgroundGradient'
  | 'backgroundImage'
  | 'paddingTop'
  | 'paddingBottom'
  | 'containerWidth'
  | 'textAlign'
  | 'headingColor'
  | 'bodyColor'
  | 'hidden'
>>;

export interface SectionStyle {
  backgroundType: 'none' | 'color' | 'gradient' | 'image';
  backgroundColor?: string;
  backgroundGradient?: string;
  backgroundImage?: string;
  paddingTop: number;
  paddingBottom: number;
  containerWidth: 'full' | 'wide' | 'normal' | 'narrow';
  textAlign?: 'left' | 'center' | 'right';
  headingColor?: string;
  bodyColor?: string;
  hidden?: boolean;
  /** Tablet overrides; unset keys inherit desktop. */
  tablet?: SectionStylePatch;
  /** Mobile overrides; unset keys inherit tablet then desktop. */
  mobile?: SectionStylePatch;
}

export interface EditableField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'image' | 'color' | 'select' | 'number' | 'boolean' | 'richtext' | 'link';
  options?: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}

export interface SectionProps {
  content: Record<string, any>;
  style?: SectionStyle;
  className?: string;
}

// ──────────────────────────────────────────────────────────────
// Default Section Style
// ──────────────────────────────────────────────────────────────

export const DEFAULT_SECTION_STYLE: SectionStyle = {
  backgroundType: 'none',
  backgroundColor: '#ffffff',
  paddingTop: 60,
  paddingBottom: 60,
  containerWidth: 'normal',
  textAlign: 'left',
};

/** Merge desktop base with tablet/mobile patches for the active preview breakpoint. */
export function resolveSectionStyle(
  style: SectionStyle | undefined,
  breakpoint: PreviewBreakpoint = 'desktop',
): SectionStyle {
  const base: SectionStyle = { ...DEFAULT_SECTION_STYLE, ...(style ?? {}) };
  if (breakpoint === 'desktop') {
    const { tablet: _t, mobile: _m, ...rest } = base;
    return rest;
  }
  const tabletPatch = style?.tablet ?? {};
  const withTablet: SectionStyle = { ...base, ...tabletPatch };
  if (breakpoint === 'tablet') {
    const { tablet: _t, mobile: _m, ...rest } = withTablet;
    return rest;
  }
  const mobilePatch = style?.mobile ?? {};
  const withMobile: SectionStyle = { ...withTablet, ...mobilePatch };
  const { tablet: _t, mobile: _m, ...rest } = withMobile;
  return rest;
}

// ──────────────────────────────────────────────────────────────
// Section Category Definitions
// ──────────────────────────────────────────────────────────────

export const SECTION_CATEGORIES: { id: SectionCategory; label: string; description: string }[] = [
  { id: 'header', label: 'Headers', description: 'Navigation bars and top bars' },
  { id: 'hero', label: 'Hero Sections', description: 'First impression sections with headlines' },
  { id: 'fleet', label: 'Fleet', description: 'Live vehicles from the agency fleet' },
  { id: 'features', label: 'Features & Benefits', description: 'Showcase your value propositions' },
  { id: 'testimonials', label: 'Testimonials', description: 'Social proof and customer reviews' },
  { id: 'pricing', label: 'Pricing Tables', description: 'Plans and pricing comparisons' },
  { id: 'contact', label: 'Contact', description: 'Contact details and a working lead form' },
  { id: 'faq', label: 'FAQ', description: 'Answers to common customer questions' },
  { id: 'gallery', label: 'Gallery', description: 'Photo grids and mosaics' },
  { id: 'steps', label: 'Steps', description: 'Explain how the rental process works' },
  { id: 'stats', label: 'Stats', description: 'Numbers and proof points' },
  { id: 'location', label: 'Location', description: 'Address, hours and an embedded map' },
  { id: 'cta', label: 'Call to Action', description: 'Conversion-focused sections' },
  { id: 'footer', label: 'Footers', description: 'Site footers with links and info' },
  { id: 'custom', label: 'Custom', description: 'Imported HTML and CSS sections' },
];

// ──────────────────────────────────────────────────────────────
// Helper: Create Section Instance
// ──────────────────────────────────────────────────────────────

export function createSectionId(): string {
  return `section_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createSectionInstance(
  category: SectionCategory,
  variationId: string,
  defaultProps: Record<string, any>,
  order: number
): SectionInstance {
  return {
    id: createSectionId(),
    category,
    variationId,
    props: { ...defaultProps },
    order,
    style: { ...DEFAULT_SECTION_STYLE },
  };
}

// ──────────────────────────────────────────────────────────────
// Section Component Renderers (Inline for now)
// ──────────────────────────────────────────────────────────────

// These will be implemented as separate components
export const SectionRendererRegistry: Record<string, React.ComponentType<SectionProps>> = {};

// Register a section renderer
export function registerSectionRenderer(key: string, component: React.ComponentType<SectionProps>) {
  SectionRendererRegistry[key] = component;
}

// Get renderer for a section
export function getSectionRenderer(category: SectionCategory, variationId: string): React.ComponentType<SectionProps> | null {
  return SectionRendererRegistry[`${category}_${variationId}`] || null;
}